import {timingFunctions} from 'polished';
import React, {FC} from 'react';
import styled, {css} from 'styled-components';

import {hoverSelector} from '../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {center} from '../../../../../libs/shared/ui/src/styles/mixins';
import {EmojiDefinition} from '../../../../services/emojis/emojis';
import {SkinTonesEnum} from '../../../../services/emojis/emojiTypes';
import {Emoji} from '../../emoji/emoji';
import {EmojiSelectHandler} from '../emoji/emojiDropdown';
import {EmojiDropdownContainer} from '../emoji/emojiDropdownContainer';

/*
 * Props.
 */

interface TagEditorEmojiProps {
  emoji: EmojiDefinition;
  skinTone?: SkinTonesEnum;
  onEmojiSelect: EmojiSelectHandler;
  isSelected: boolean;
  isDisabled?: boolean;
}

/*
 * Style.
 */

const StyledWrapperDiv = styled.div`
  position: relative;
`;

interface BoxDivProps {
  $isSelected: boolean;
  $isOpen: boolean;
}

const StyledBoxDiv = styled.div<BoxDivProps>`
  width: 22px;
  height: 22px;

  border-radius: 50%;
  opacity: 0.3;

  transition-duration: 150ms;
  transition-property: transform;
  transition-timing-function: ${timingFunctions('easeInBack')};

  ${center()};

  /* Apply the same active styles on selected, open, or hover. */
  ${(p) => maybeAddActiveStyles(p)}
  ${hoverSelector} {
    ${addActiveStyles()}
  }
`;

function maybeAddActiveStyles(sc: BoxDivProps) {
  if (!sc.$isOpen && !sc.$isSelected) {
    return '';
  }

  return addActiveStyles();
}

function addActiveStyles() {
  return css`
    opacity: 1;
    transform: scale(1.2);
    transition-timing-function: ${timingFunctions('easeOutBack')};
  `;
}

/*
 * Component.
 */

export const TagEditorEmoji: FC<TagEditorEmojiProps> = (props) => (
  <PopoverCoordinator
    isDisabled={props.isDisabled}
    renderButton={(isOpen) => renderButton(props, isOpen)}
    renderModal={(modalProps) => renderDropdown(modalProps, props)}
  />
);

/*
 * Button.
 */

function renderButton(props: TagEditorEmojiProps, isOpen: boolean) {
  const {isSelected} = props;
  return (
    <StyledWrapperDiv>
      <StyledBoxDiv $isSelected={isSelected} $isOpen={isOpen}>
        <Emoji emoji={props.emoji} size={22} skinTone={props.skinTone} />
      </StyledBoxDiv>
    </StyledWrapperDiv>
  );
}

/*
 * Dropdown.
 */

function renderDropdown(modalProps: PopoverTransferProps, props: TagEditorEmojiProps) {
  return (
    <EmojiDropdownContainer
      {...modalProps}
      onEmojiSelect={modalProps.wrap(props.onEmojiSelect)}
      showCustomEmojiCategory={false}
    />
  );
}
